import { ConfirmationModal, MessageIcon } from '@components'
import settings from '@config/settings'
import cn from '@integrations/classnames'
import { useContext, useEffect, useRef, useState } from '@integrations/react'
import { useLocation, useNavigate } from '@integrations/routing'
import { uiSize } from '@utils'
import { LoginStatuses, paths, selectCurrentUserStatus, useAppSelector } from 'mmfintech-backend-api'
import { GlobalContext, tr, useDropDownMenu } from 'mmfintech-commons'
import CreateTicketModal from '../CreateTicketModal/CreateTicketModal'
import styles from './SupportFab.module.scss'

const SupportIcon = () => <MessageIcon strokeLinecap='round' strokeLinejoin='round' />

const CloseIcon = () => (
  <svg
    width={uiSize(20)}
    height={uiSize(20)}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
  >
    <line x1='6' y1='6' x2='18' y2='18' />
    <line x1='18' y1='6' x2='6' y2='18' />
  </svg>
)

const EmailIcon = () => (
  <svg
    width={uiSize(20)}
    height={uiSize(20)}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='2' y='4' width='20' height='16' rx='2' />
    <path d='m22 6-10 7L2 6' />
  </svg>
)

function SupportFab() {
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [showLoginHint, setShowLoginHint] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { modalShow, modalHide } = useContext(GlobalContext)

  const userStatus = useAppSelector(selectCurrentUserStatus)
  const isLoggedIn = userStatus === LoginStatuses.LOGGED_IN
  const isOnSupportPage = pathname.startsWith('/support')
  const isOnLoginPage = pathname === paths.login()
  const isOnAuthPage = pathname.startsWith(paths.login()) || pathname.startsWith(paths.signUp())

  const { visible, toggleDropDown, hideDropDown, setRef } = useDropDownMenu()
  const triggerRef = useRef<HTMLButtonElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear route-specific UI after navigation
  useEffect(() => {
    setShowLoginHint(false)
  }, [pathname])

  useEffect(() => {
    if (!visible) setShowLoginHint(false)
  }, [visible])

  // useDropDownMenu's own Escape handling already closes the dropdown — this listener runs
  // alongside it purely to return focus to the trigger, which the hook doesn't do.
  useEffect(() => {
    if (!visible) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        hideDropDown()
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [visible, hideDropDown])

  // Temporarily keep support out of the authentication flow.
  if (isOnSupportPage || isOnAuthPage) return null

  function handleTicketSuccess(ticketId: number) {
    setShowTicketModal(false)
    navigate(paths.support.ticketDetail(ticketId))
  }

  function handleCustomerServiceClick() {
    if (isOnLoginPage) {
      setShowLoginHint(true)
      return
    }

    modalShow({
      content: (
        <ConfirmationModal
          title={tr('FRONTEND.SUPPORT.LOGIN_REQUIRED.TITLE', 'Login required')}
          question={tr(
            'FRONTEND.SUPPORT.LOGIN_REQUIRED.MESSAGE',
            'You need to log in to your account to contact customer service.'
          )}
          confirmCaption={tr('FRONTEND.SUPPORT.LOGIN_REQUIRED.GO_TO_LOGIN', 'Go to log in')}
          onConfirm={() => navigate(paths.login())}
          onClose={modalHide}
        />
      )
    })
  }

  if (isLoggedIn) {
    return (
      <>
        <button
          type='button'
          className={cn(styles['support-fab-btn'], 'support-fab-btn')}
          onClick={() => setShowTicketModal(true)}
          title={tr('FRONTEND.SUPPORT.FAB.TITLE', 'Support')}
        >
          <SupportIcon />
        </button>

        {showTicketModal && (
          <CreateTicketModal onSuccess={handleTicketSuccess} onClose={() => setShowTicketModal(false)} />
        )}
      </>
    )
  }

  const items = [
    {
      id: 'email',
      icon: <EmailIcon />,
      label: tr('FRONTEND.SUPPORT.FAB.EMAIL_LABEL', 'Send us an email'),
      href: settings.customerServiceUrl
    },
    {
      id: 'customer-service',
      icon: <SupportIcon />,
      label: (
        <>
          {tr('FRONTEND.SUPPORT.FAB.CUSTOMER_SERVICE_LABEL', 'Contact customer service')}
          {showLoginHint && (
            <span className={cn(styles['login-hint'], 'login-hint')}>
              {tr(
                'FRONTEND.SUPPORT.LOGIN_REQUIRED.MESSAGE',
                'You need to log in to your account to contact customer service.'
              )}
            </span>
          )}
        </>
      ),
      onClick: handleCustomerServiceClick,
      keepOpenOnClick: isOnLoginPage
    }
  ]

  return (
    <div
      className={cn(styles['support-fab'], 'support-fab', {
        [styles['support-fab-auth']]: isOnAuthPage,
        'support-fab-auth': isOnAuthPage
      })}
      ref={setRef}
    >
      {/* Trigger renders before the dropdown in the DOM (visual position comes entirely from
          `position: absolute` in the .module.scss, independent of DOM order) so that Tab,
          pressed right after opening — while focus is still on the trigger — moves forward
          INTO the menu items instead of skipping past them to whatever's next on the page. */}
      <button
        ref={triggerRef}
        type='button'
        className={cn(styles['support-fab-btn'], 'support-fab-btn')}
        aria-haspopup='menu'
        aria-expanded={visible}
        aria-label={tr('FRONTEND.SUPPORT.FAB.TITLE', 'Support')}
        onClick={toggleDropDown}
      >
        {visible ? <CloseIcon /> : <SupportIcon />}
      </button>
      <div
        role='menu'
        aria-hidden={!visible}
        className={cn(styles['support-fab-dropdown'], 'support-fab-dropdown', {
          [styles['support-fab-dropdown-open']]: visible,
          'support-fab-dropdown-open': visible
        })}
      >
        {items.map((item) =>
          item.href ? (
            <a
              key={item.id}
              role='menuitem'
              href={item.href}
              className={cn(styles['support-fab-item'], 'support-fab-item')}
              onClick={hideDropDown}
            >
              <span className={cn(styles['support-fab-item-icon'], 'support-fab-item-icon')}>{item.icon}</span>
              <span className={cn(styles['support-fab-item-label'], 'support-fab-item-label')}>{item.label}</span>
            </a>
          ) : (
            <button
              key={item.id}
              type='button'
              role='menuitem'
              className={cn(styles['support-fab-item'], 'support-fab-item')}
              onClick={() => {
                if (!item.keepOpenOnClick) hideDropDown()
                item.onClick?.()
              }}
            >
              <span className={cn(styles['support-fab-item-icon'], 'support-fab-item-icon')}>{item.icon}</span>
              <span className={cn(styles['support-fab-item-label'], 'support-fab-item-label')}>{item.label}</span>
            </button>
          )
        )}
      </div>
    </div>
  )
}

export default SupportFab
